import './styles/main.css';
import { getSession, onAuthChange, verifyEmailLink } from '@/lib/auth';
import { createRouter } from '@/lib/router';
import { renderSettings } from '@/views/settings';
import { primeVoiceQuota } from '@/lib/voice/VoiceInput';
import { renderSignIn } from '@/views/sign-in';
import { renderShell } from '@/views/app-shell';
import { setShell } from '@/lib/shell-handle';
import { renderProjectsList } from '@/views/projects-list';
import { renderProjectDetail } from '@/views/project-detail';
import { renderRoomDetail } from '@/views/room-detail';
import { renderShareView } from '@/views/share-view';
import { renderFirmNotice, renderNoSuchFirm } from '@/views/firm-notice';
import { hostSlug, setHostFirmBrand, refuseAsUnknownFirm, hostMismatchNotice } from '@/lib/host-branding';
import { setCurrentFirm } from '@/lib/current-firm';
import { forgetAllProjects } from '@/lib/data/project-workspace';
import { clearExportBranding } from '@/lib/export';
import { ensureBrandFont } from '@/lib/brand-font-loader';
import { setTitleBase } from '@/lib/dom';
import { fetchMyMembership, amIPlatformAdmin, fetchPublicFirmBrand } from '@/lib/data/firms';
import { mySupportSession } from '@/lib/data/admin';
import { renderAdmin } from '@/views/admin';
import { watchForNewVersion, showUpdateBar } from '@/lib/app-version';
import { renderMaterialLibrary } from '@/views/material-library';
import { renderContactsLibrary } from '@/views/contacts-library';
import { mountSupportBanner } from '@/views/support-banner';
import { firmAccess, type MembershipLookup } from '@/lib/firm-access';
import { renderContractorView } from '@/views/contractor-view';
import { describeError, fatalMarkup } from '@/lib/fatal';
import {
  readAuthRedirect, bootMode, resetFailureNotice, pendingPurpose, linkFailureNotice, RECOVERY_PENDING_KEY,
  type AuthRedirect, type PasswordPurpose,
} from '@/lib/auth-redirect';
import { renderUpdatePassword } from '@/views/update-password';
import { renderConfirmLink } from '@/views/confirm-link';
import { renderForgotPassword } from '@/views/forgot-password';
import { toast } from '@/lib/toast';

const root = document.querySelector<HTMLDivElement>('#app')!;

/**
 * Show a crash instead of a blank page.
 *
 * Targets the shell's main area when there is one, so the nav survives and the
 * user can navigate away; falls back to the whole root pre-shell.
 */
function showFatal(err: unknown, target?: HTMLElement): void {
  const report = describeError(err);
  // The console is still the place with the stack; the card carries the message.
  console.error('[fatal]', err);
  const host = target ?? document.querySelector<HTMLElement>('.app-main') ?? root;
  host.innerHTML = fatalMarkup(report);
  host.querySelectorAll('[data-action="fatal-reload"]').forEach((el) =>
    el.addEventListener('click', () => { location.reload(); }));
}

/**
 * Routes render fire-and-forget, so a rejection inside one used to vanish into
 * an unhandled rejection and leave the content area empty with no explanation.
 * Every `void render(...)` goes through here instead.
 */
function guard(p: unknown, target?: HTMLElement): void {
  if (p instanceof Promise) void p.catch((e: unknown) => showFatal(e, target));
}

// Anything that still escapes — a listener that throws after render, a rejected
// promise nobody awaited — surfaces rather than silently blanking the view.
window.addEventListener('unhandledrejection', (e) => {
  e.preventDefault();
  showFatal(e.reason);
});
window.addEventListener('error', (e) => {
  if (e.error !== undefined) showFatal(e.error);
});

/**
 * Read at module load, BEFORE anything touches the Supabase client. The client
 * consumes a reset link's tokens during startup and clears the fragment, so
 * this is the only moment the link is still readable. Consumed once by the
 * first boot(); a later re-boot (portal navigation) must not re-apply it.
 * See @/lib/auth-redirect.
 */
let pendingRedirect: AuthRedirect = readAuthRedirect(location.hash);

/**
 * True while the set-password screen is up. The recovery session emits its own
 * auth events — PASSWORD_RECOVERY on a setTimeout, INITIAL_SESSION on
 * subscribe, USER_UPDATED on save — and each would otherwise replace this
 * screen with the app before a new password was chosen.
 */
let recoveryInProgress = false;

function setRecoveryPending(on: boolean, purpose: PasswordPurpose = 'recovery'): void {
  try {
    if (on) sessionStorage.setItem(RECOVERY_PENDING_KEY, purpose);
    else sessionStorage.removeItem(RECOVERY_PENDING_KEY);
  } catch { /* storage unavailable: a reload just won't resume the reset */ }
}
function recoveryPurpose(): PasswordPurpose | null {
  try { return pendingPurpose(sessionStorage.getItem(RECOVERY_PENDING_KEY)); } catch { return null; }
}
function recoveryPending(): boolean {
  return recoveryPurpose() !== null;
}
/** Drop auth parameters from the URL without firing hashchange. */
function clearAuthFragment(): void {
  if (readAuthRedirect(location.hash).kind !== 'none' || location.hash === '#') {
    history.replaceState(history.state, '', location.pathname + location.search);
  }
}

/**
 * How long boot waits to learn whose address this is. Bounded: an unanswered
 * lookup falls back to the neutral brand rather than holding the page.
 */
const HOST_LOOKUP_MS = 4000;

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('timeout')), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
  });
}

let currentUserId: string | null = null;
let booted = false;
let shareToken: string | null = null;
let contractorToken: string | null = null;

function readShareTokenFromHash(): string | null {
  const m = /^#\/share\/([^/?#]+)/.exec(location.hash);
  return m ? decodeURIComponent(m[1]) : null;
}

function readContractorTokenFromHash(): string | null {
  const m = /^#\/contractor\/([^/?#]+)/.exec(location.hash);
  return m ? decodeURIComponent(m[1]) : null;
}

function isPortalHash(hash: string): boolean {
  return hash.startsWith('#/share/') || hash.startsWith('#/contractor/');
}

async function boot(): Promise<void> {
  const redirect = pendingRedirect;
  pendingRedirect = { kind: 'none' };

  // Share + contractor routes bypass auth entirely.
  shareToken = readShareTokenFromHash();
  contractorToken = readContractorTokenFromHash();
  window.addEventListener('hashchange', () => {
    const nextShare = readShareTokenFromHash();
    const nextContractor = readContractorTokenFromHash();
    if (nextShare !== shareToken || nextContractor !== contractorToken) {
      shareToken = nextShare;
      contractorToken = nextContractor;
      booted = false;
      currentUserId = '___reset___';
      void boot();
    }
  });

  if (shareToken) {
    guard(renderShareView(root, shareToken));
    return;
  }
  if (contractorToken) {
    guard(renderContractorView(root, contractorToken));
    return;
  }

  // Whose address is this? Looked up before any signed-out page renders, so
  // the sign-in page wears the right firm's brand — or refuses an address no
  // firm uses. Portal links (above) never depend on it.
  const slug = hostSlug(location.hostname);
  if (slug) {
    try {
      const hostFirm = await withTimeout(fetchPublicFirmBrand(slug), HOST_LOOKUP_MS);
      setHostFirmBrand(slug, hostFirm);
      // A signed-out page renders synchronously from what the lookup found, so
      // the firm's typeface has to be asked for now (0045).
      ensureBrandFont(hostFirm?.brand_font);
    } catch { /* unanswered: neutral brand, carry on */ }
    if (refuseAsUnknownFirm(location.hostname)) {
      renderNoSuchFirm(root);
      return;
    }
  }

  if (redirect.kind === 'recovery' || redirect.kind === 'invite') setRecoveryPending(true, redirect.kind);

  const session = await getSession();
  const uid = session?.user.id ?? null;
  const email = session?.user.email ?? null;

  switch (bootMode(redirect, session !== null, recoveryPending())) {
    case 'update-password':
      // Set BEFORE subscribing below, so no recovery event can slip through.
      recoveryInProgress = true;
      clearAuthFragment();
      showSetPassword(recoveryPurpose() ?? 'recovery', uid, email);
      break;

    case 'confirm-link': {
      if (redirect.kind !== 'confirm') break;
      const { tokenHash, purpose } = redirect;
      // Out of the address bar at once: the token is a one-time credential.
      clearAuthFragment();
      // Hold every auth event until this flow ends — INITIAL_SESSION on
      // subscribe (below) and SIGNED_IN from verifyOtp would each replace
      // this page with the app. Same guard as the set-password page.
      recoveryInProgress = true;
      renderConfirmLink(root, {
        purpose,
        onContinue: async () => {
          try {
            const who = await verifyEmailLink(tokenHash, purpose);
            setRecoveryPending(true, purpose);
            showSetPassword(purpose, who.userId, who.email);
          } catch (err) {
            recoveryInProgress = false;
            booted = true;
            currentUserId = uid;
            renderForgotPassword(root, {
              notice: linkFailureNotice(purpose, err),
              onBack: () => { booted = false; applyAuthState(uid, email); },
            });
          }
        },
      });
      break;
    }

    case 'reset-link-failed':
      setRecoveryPending(false);
      clearAuthFragment();
      // Record this auth state as already handled. The subscription below is
      // sent an INITIAL_SESSION event right away, and applyAuthState() would
      // otherwise answer it by replacing this notice with the sign-in page.
      booted = true;
      currentUserId = uid;
      renderForgotPassword(root, {
        notice: resetFailureNotice(redirect),
        onBack: () => { booted = false; applyAuthState(uid, email); },
      });
      break;

    case 'normal':
      // A leftover flag with no session is an abandoned reset. Clear it, or the
      // next reload after a normal sign-in would land on the set-password
      // screen for no reason (bootMode: pending + session → update-password).
      setRecoveryPending(false);
      applyAuthState(uid, email);
      break;
  }

  onAuthChange((user) => {
    if (shareToken || contractorToken) return; // ignore auth changes while in a portal
    if (recoveryInProgress) return;            // see recoveryInProgress
    applyAuthState(user?.id ?? null, user?.email ?? null);
  });
}

/**
 * Choose a password — after a reset link, or to finish accepting an
 * invitation. `recoveryInProgress` must already be true.
 */
function showSetPassword(purpose: PasswordPurpose, uid: string | null, email: string | null): void {
  renderUpdatePassword(root, {
    purpose,
    onDone: () => {
      recoveryInProgress = false;
      setRecoveryPending(false);
      toast(purpose === 'invite' ? 'Welcome to Design Studio HQ.' : 'Password updated.', 'success');
      booted = false;
      applyAuthState(uid, email);
    },
    onCancelled: () => {
      recoveryInProgress = false;
      setRecoveryPending(false);
      applyAuthState(null, null);
    },
  });
}

/**
 * Drop everything one signed-in session left in memory.
 *
 * Only the caches that are SCOPED TO A FIRM: the branding printed on exports
 * (cached once per session on purpose) and the project workspace. A font
 * already fetched is not firm data and can stay.
 */
function forgetSession(): void {
  clearExportBranding();
  forgetAllProjects();
  setCurrentFirm(null);
}

function applyAuthState(userId: string | null, email: string | null): void {
  if (booted && userId === currentUserId) return;
  booted = true;
  currentUserId = userId;

  if (!userId) {
    // Signing out does NOT reload the page — this renders the sign-in screen
    // in the same tab, so anything held in module state outlives the session.
    // The next person to sign in on this laptop must not inherit the last
    // firm's brand on a printed export, or see a cached project repaint
    // before the server refuses it.
    forgetSession();
    if (location.hash && !isPortalHash(location.hash)) location.hash = '';
    renderSignIn(root);
    return;
  }

  void enterApp(userId, email ?? '');
}

/**
 * Signed in: check the person has a usable firm (Phase 22), then build the
 * app. Without one, every data rule returns nothing — so say why instead of
 * showing an empty app. See src/lib/firm-access.ts for why a failed check
 * lets them in.
 */
async function enterApp(userId: string, email: string): Promise<void> {
  let lookup: MembershipLookup;
  try {
    const membership = await fetchMyMembership();
    // Only asked when there's no firm: it's the one case the answer changes.
    const isPlatformAdmin = membership ? false : await amIPlatformAdmin().catch(() => false);
    const supportSession = isPlatformAdmin ? await mySupportSession().catch(() => null) : null;
    lookup = { ok: true, membership, isPlatformAdmin, supportSession };
  } catch {
    lookup = { ok: false };
  }
  // Signed out, or into another account, while the check was in flight.
  if (currentUserId !== userId) return;

  const access = firmAccess(lookup);
  if (access.kind === 'platform-admin') {
    // Starting a support session reloads, rather than rebuilding the app on
    // top of this page: a clean start is the simplest way to get the router
    // and shell exactly once.
    void renderAdmin(root, { email, onSupportStarted: () => { location.hash = ''; location.reload(); } });
    return;
  }
  if (access.kind !== 'ok' && access.kind !== 'support') {
    renderFirmNotice(root, access, email);
    return;
  }

  // Whose firm this is, for the header, tab title and the links it builds.
  const firm = access.kind === 'support'
    ? { id: access.session.firm_id, name: access.session.firm_name, slug: null }
    : access.membership
      ? { id: access.membership.firm.id, name: access.membership.firm.name, slug: access.membership.firm.slug }
      : null;
  setCurrentFirm(firm);
  if (firm) setTitleBase(firm.name);

  const shell = renderShell(root, email, firm?.name);
  if (access.kind === 'support') mountSupportBanner(root, access.session);
  if (access.kind === 'ok' && firm) {
    const notice = hostMismatchNotice(location.hostname, firm.slug, firm.name);
    if (notice) toast(notice, 'info', 10_000);
  }
  // Warm the dictation quota so the first mic tap doesn't wait on a round
  // trip. Failure is silent — the mic re-checks before recording.
  void primeVoiceQuota().catch(() => { /* mic checks again on use */ });
  setShell(shell);
  const main = shell.main;
  const router = createRouter(
    [
      { pattern: '/', render: () => { location.hash = '#/projects'; } },
      { pattern: '/projects', render: () => { guard(renderProjectsList(main), main); } },
      { pattern: '/settings', render: () => { guard(renderSettings(main), main); } },
      { pattern: '/library', render: () => { guard(renderMaterialLibrary(main), main); } },
      { pattern: '/contacts', render: () => { guard(renderContactsLibrary(main), main); } },
      { pattern: '/projects/:id', render: ({ id }) => { guard(renderProjectDetail(main, id, 'dashboard'), main); } },
      { pattern: '/projects/:id/:section', render: ({ id, section }) => { guard(renderProjectDetail(main, id, section), main); } },
      { pattern: '/rooms/:id', render: ({ id }) => { guard(renderRoomDetail(main, id), main); } },
    ],
    () => { main.innerHTML = `<section class="page"><div class="empty"><p>Page not found.</p></div></section>`; },
  );
  router.start();
}


// A tab left open across a deploy keeps running old code; offer the new one.
watchForNewVersion(() => { showUpdateBar(); });

boot().catch((err) => {
  root.innerHTML = `<div class="boot"><div class="boot-card"><h1>Boot error</h1><div class="status error">${err instanceof Error ? err.message : String(err)}</div></div></div>`;
});
